import { useCallback, useEffect, useState } from "react";
import { Input, Spin, Table, Tag, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { AdminListShell } from "../components/admin/AdminListShell";
import { fetchAdminJobRunsPage } from "../api/client";
import type { AdminJobRunApiRow } from "../api/types";
import { fmtDateTime } from "../lib/formatAdmin";
import { jobLabelZh, jobSummaryToZh } from "../lib/regulatoryJobsDisplay";

export function JobRunsPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [jobKey, setJobKey] = useState("");
  const [items, setItems] = useState<AdminJobRunApiRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<"forbidden" | "error" | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const r = await fetchAdminJobRunsPage({
      page,
      page_size: pageSize,
      job_key: jobKey || undefined,
    });
    if (r === "forbidden") setErr("forbidden");
    else if (r === "error") setErr("error");
    else {
      setItems(r.items);
      setTotal(r.total);
    }
    setLoading(false);
  }, [page, pageSize, jobKey]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [jobKey]);

  const columns: TableColumnsType<AdminJobRunApiRow> = [
    {
      title: "任务",
      key: "job",
      width: 220,
      render: (_, row) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {row.job_label_zh ?? jobLabelZh(row.job_key)}
          </div>
          <div className="admin-ledger-mono">{row.job_key}</div>
        </div>
      ),
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 88,
      render: (s: string) => <Tag>{s}</Tag>,
    },
    { title: "退出码", dataIndex: "exit_code", width: 80, render: (c) => c ?? "—" },
    {
      title: "摘要",
      key: "sum",
      ellipsis: true,
      render: (_, row) => {
        const zh = row.summary_zh ?? jobSummaryToZh(row.summary);
        const show = zh ?? row.summary ?? "—";
        const raw = row.summary;
        if (raw && zh && raw !== zh)
          return (
            <Tooltip title={`原始：${raw}`}>
              <span>{show}</span>
            </Tooltip>
          );
        return show;
      },
    },
    { title: "开始", dataIndex: "started_at", width: 158, render: fmtDateTime },
    { title: "结束", dataIndex: "finished_at", width: 158, render: fmtDateTime },
    { title: "主机", dataIndex: "host", width: 120, ellipsis: true, render: (h) => h ?? "—" },
  ];

  return (
    <AdminListShell
      title="任务监控"
      description="监管数据同步与邮件派发等定时任务执行记录（全量分页）。"
      err={err}
      errorBanner
      toolbar={
        <div className="admin-ledger-toolbar">
          <Input
            placeholder="job_key 精确过滤，如 openfda_ingest"
            value={jobKey}
            onChange={(e) => setJobKey(e.target.value)}
            variant="borderless"
            style={{ width: 280 }}
            allowClear
          />
        </div>
      }
      table={(bodyY) => (
        <div className="admin-ledger-table-wrap admin-ledger-table-wrap--flex">
          <Spin spinning={loading}>
            <Table<AdminJobRunApiRow>
              rowKey="id"
              className="admin-ledger-table"
              columns={columns}
              dataSource={items}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50],
                showTotal: (t) => `共 ${t} 条`,
                onChange: (p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                },
              }}
              scroll={{ x: 1280, y: bodyY }}
              locale={{ emptyText: "暂无记录" }}
            />
          </Spin>
        </div>
      )}
    />
  );
}
